/* eslint-env browser */
import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import {
  Router,
  Route,
  IndexRoute,
  IndexRedirect,
  browserHistory,
} from 'react-router';
import { connect } from 'react-redux';
import { Flex, Box } from 'reflexbox';

import actions from 'frontend/state/actions';

import {
  Loader,
} from 'frontend/components';

import { retry } from 'frontend/util';

import Raidbots from './Raidbots';

import Simbot from './Simbot/index';
import Tabs from './Simbot/Tabs';

function onRouteUpdate(onRouteChange) {
  window.scrollTo(0, 0);

  onRouteChange();
}

const modules = [{
  name: 'SimbotHome',
  import: () => import(/* webpackChunkName: "home" */ './Simbot/Home'),
}, {
  name: 'SimbotReport',
  import: () => import(/* webpackChunkName: "report" */ './Simbot/Report'),
}, {
  name: 'SimbotGearOptimizer',
  import: () => import(/* webpackChunkName: "optimize" */ './Simbot/Optimize'),
}, {
  name: 'SimbotDroptimizer',
  import: () => import(/* webpackChunkName: "droptimizer" */ './Simbot/Droptimizer'),
}, {
  name: 'SimbotStatWeights',
  import: () => import(/* webpackChunkName: "statweights" */ './Simbot/StatWeights'),
}, {
  name: 'SimbotQuickSim',
  import: () => import(/* webpackChunkName: "quick" */ './Simbot/QuickSim'),
}, {
  name: 'SimbotAdvancedMode',
  import: () => import(/* webpackChunkName: "advancedMode" */ './Simbot/AdvancedMode'),
}, {
  name: 'SimbotReportBanner',
  import: () => import(/* webpackChunkName: "banner" */ './Simbot/Report/Banner'),
}, {
  name: 'TalentTreeRender',
  import: () => import(/* webpackChunkName: "talentTreeRender" */ './Simbot/Render/TalentTree'),
}, {
  name: 'SimbotSimcReport',
  import: () => import(/* webpackChunkName: "simcReport" */ './Simbot/Report/SimcReport'),
}, {
  name: 'UserAccount',
  import: () => import(/* webpackChunkName: "user" */ './UserAccount'),
}, {
  name: 'StandaloneAuth',
  import: () => import(/* webpackChunkName: "standalone" */ './Auth/Standalone'),
}, {
  name: 'Ohshi',
  import: () => import(/* webpackChunkName: "ohshi" */ './Ohshi'),
}, {

  // no preload

  name: 'SimbotGearCompare',
  import: () => import(/* webpackChunkName: "gearCompare" */ './Simbot/GearCompare'),
  preload: false,
}, {
  name: 'SimbotTalentsCompare',
  import: () => import(/* webpackChunkName: "talentsCompare" */ './Simbot/TalentsCompare'),
  preload: false,
}, {
  name: 'PasswordReset',
  import: () => import(/* webpackChunkName: "passwordReset" */ './PasswordReset'),
  preload: false,
}, {
  name: 'EmailVerified',
  import: () => import(/* webpackChunkName: "emailVerified" */ './Pages/EmailVerified'),
  preload: false,
}, {
  name: 'EmailVerificationFailed',
  import: () => import(/* webpackChunkName: "emailVerified" */ './Pages/EmailVerificationFailed'),
  preload: false,
}, {
  name: 'AccountDelete',
  import: () => import(/* webpackChunkName: "accountDelete" */ './AccountDelete'),
  preload: false,
}, {
  name: 'PrivacyPolicyPage',
  import: () => import(/* webpackChunkName: "privacyPolicy" */ './Pages/PrivacyPolicy'),
  preload: false,
}, {
  name: 'DevelopersPage',
  import: () => import(/* webpackChunkName: "developers" */ './Pages/Developers'),
  preload: false,
}, {
  name: 'Blingtron',
  import: () => import(/* webpackChunkName: "blingtron" */ './Blingtron'),
  preload: false,
}, {
  name: 'BlingtronReport',
  import: () => import(/* webpackChunkName: "blingtronReport" */ './Blingtron/Report'),
  preload: false,
}, {
  name: 'PremiumInfo',
  import: () => import(/* webpackChunkName: "premiumInfo" */ './PremiumInfo'),
  preload: false,
}, {
  name: 'ResetSite',
  import: () => import(/* webpackChunkName: "resetSite" */ './ResetSite'),
  preload: false,
}, {
  name: 'Admin',
  import: () => import(/* webpackChunkName: "admin" */ './Admin'),
  preload: false,
}];

const LoaderCenter = (
  <Flex align="center" justify="center" mt={2} style={{ minHeight: 200 }}>
    <Box>
      <Loader />
    </Box>
  </Flex>
);

const LazyModule = name => {
  const module = _.find(modules, { name });
  const Component = React.lazy(() => retry(module.import));
  return props => (
    <React.Suspense fallback={LoaderCenter}>
      <Component {...props} />
    </React.Suspense>
  );
};

const withTabs = Component => () => (
  <>
    <Tabs />
    <Component />
  </>
);

const RaidbotsRouter = ({ onRouteChange }) => (
  <Router history={browserHistory} onUpdate={() => onRouteUpdate(onRouteChange)}>
    <Route path="/" component={Raidbots}>
      <IndexRedirect to="/simbot" />
      <Route path="simbot" component={Simbot}>
        <IndexRoute component={LazyModule('SimbotHome')} />

        <Route path="stats" component={withTabs(LazyModule('SimbotStatWeights'))} />
        <Route path="topgear" component={withTabs(LazyModule('SimbotGearOptimizer'))} />
        <Route path="droptimizer" component={withTabs(LazyModule('SimbotDroptimizer'))} />
        <Route path="gear" component={withTabs(LazyModule('SimbotGearCompare'))} />
        <Route path="talents" component={withTabs(LazyModule('SimbotTalentsCompare'))} />
        <Route path="quick" component={withTabs(LazyModule('SimbotQuickSim'))} />
        <Route path="advanced" component={withTabs(LazyModule('SimbotAdvancedMode'))} />

        <Route path="report/:simId" component={LazyModule('SimbotReport')} />
      </Route>
      <Route path="premium" component={LazyModule('PremiumInfo')} />
      <Route path="privacy" component={LazyModule('PrivacyPolicyPage')} />
      <Route path="developers" component={LazyModule('DevelopersPage')} />
      <Route path="account" component={LazyModule('UserAccount')} />
      <Route path="auth" component={LazyModule('StandaloneAuth')} />
      <Route path="password-reset/:resetToken" component={LazyModule('PasswordReset')} />
      <Route path="passwordreset/:resetToken" component={LazyModule('PasswordReset')} />
      <Route path="account-delete/:userId/:token" component={LazyModule('AccountDelete')} />
      <Route path="email-verified" component={LazyModule('EmailVerified')} />
      <Route path="email-verification-failed" component={LazyModule('EmailVerificationFailed')} />
      <Route path="blingtron" component={LazyModule('Blingtron')} />
      <Route path="grommash-hold" component={LazyModule('Admin')} />
      <Route path="ohshi" component={LazyModule('Ohshi')} />
      <Route path="reset" component={LazyModule('ResetSite')} />
    </Route>
    <Route path="/blingtron/report/:simId" component={LazyModule('BlingtronReport')} />
    <Route path="/simbot/report/:simId/simc" component={LazyModule('SimbotSimcReport')} />
    <Route path="/simbot/report/:simId/banner" component={LazyModule('SimbotReportBanner')} />
    <Route path="/simbot/render/talents/:talentString" component={LazyModule('TalentTreeRender')} />
  </Router>
);

RaidbotsRouter.propTypes = {
  onRouteChange: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  onRouteChange: actions.changeRoute,
};

export default connect(null, mapDispatchToProps)(RaidbotsRouter);
